// src/components/common/MarkdownRenderer.jsx
// Handles: # h1, ## h2, ### h3, **bold**, *italic*, `code`,
//          > blockquote, - bullets, 1. numbered, ---, 
//          | markdown tables |  ← NEW

const md = {
  root:       { fontSize: 14, lineHeight: 1.8, color: "#1a1a2e" },
  p:          { margin: "0 0 10px 0", lineHeight: 1.8 },
  h1:         { fontSize: 17, fontWeight: 700, margin: "0 0 12px 0", color: "#0d1117", borderBottom: "2px solid #0972d3", paddingBottom: 6 },
  h2:         { fontSize: 15, fontWeight: 700, margin: "16px 0 6px 0", color: "#0d1117", borderBottom: "1px solid #e5e7eb", paddingBottom: 4 },
  h3:         { fontSize: 14, fontWeight: 700, margin: "14px 0 4px 0", color: "#0d1117" },
  ul:         { margin: "4px 0 12px 0", paddingLeft: 22, listStyleType: "disc" },
  ol:         { margin: "4px 0 12px 0", paddingLeft: 22 },
  li:         { marginBottom: 6, lineHeight: 1.7 },
  blockquote: { borderLeft: "3px solid #0972d3", background: "#f0f7ff", margin: "0 0 12px 0", padding: "8px 14px", borderRadius: "0 6px 6px 0", color: "#2c5282", fontStyle: "italic" },
  hr:         { border: "none", borderTop: "1px solid #e5e7eb", margin: "14px 0" },
  inlineCode: { background: "#f0f0f5", borderRadius: 4, padding: "1px 6px", fontSize: 12.5, fontFamily: "ui-monospace, 'Cascadia Code', monospace", color: "#c0392b", border: "1px solid #e0e0ea" },
  // Table styles
  tableWrap:  { overflowX: "auto", margin: "0 0 14px 0" },
  table:      { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  th:         { padding: "9px 16px", background: "#232f3e", color: "#fff", textAlign: "left", fontWeight: 600, whiteSpace: "nowrap" },
  td:         { padding: "8px 16px", borderBottom: "1px solid #e5e7eb" },
  trEven:     { background: "#f8f9fb" },
  trOdd:      { background: "#ffffff" },
};

export default function MarkdownRenderer({ text }) {
  if (!text) return null;

  // ── Inline parser: **bold**, *italic*, `code` ──────────────────
  const parseInline = (str) => {
    if (!str) return str;
    const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g;
    const parts = [];
    let last = 0, match, k = 0;
    while ((match = regex.exec(str)) !== null) {
      if (match.index > last) parts.push(str.slice(last, match.index));
      if (match[0].startsWith("**"))
        parts.push(<strong key={k++} style={{ fontWeight: 700 }}>{match[2]}</strong>);
      else if (match[0].startsWith("*"))
        parts.push(<em key={k++}>{match[3]}</em>);
      else
        parts.push(<code key={k++} style={md.inlineCode}>{match[4]}</code>);
      last = match.index + match[0].length;
    }
    if (last < str.length) parts.push(str.slice(last));
    return parts.length > 0 ? parts : str;
  };

  // ── Table parser ───────────────────────────────────────────────
  // Collects consecutive lines that look like | ... | ... |
  // Separator rows like |---|---| are skipped
  const parseTable = (tableLines, key) => {
    const isSeparator = (line) => /^\|[\s\-:|]+\|/.test(line);
    const parseCells  = (line) =>
      line.replace(/^\||\|$/g, "").split("|").map((c) => c.trim());

    const dataLines = tableLines.filter((l) => !isSeparator(l));
    if (dataLines.length === 0) return null;

    const headers = parseCells(dataLines[0]);
    const bodyRows = dataLines.slice(1);

    return (
      <div key={key} style={md.tableWrap}>
        <table style={md.table}>
          <thead>
            <tr>
              {headers.map((h, hi) => (
                <th key={hi} style={md.th}>{parseInline(h)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bodyRows.map((row, ri) => (
              <tr key={ri} style={ri % 2 === 0 ? md.trEven : md.trOdd}>
                {parseCells(row).map((cell, ci) => (
                  <td key={ci} style={md.td}>{parseInline(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ── Main line-by-line parser ───────────────────────────────────
  const lines   = text.split("\n");
  const elements = [];
  let key = 0, i = 0;

  while (i < lines.length) {
    const line    = lines[i];
    const trimmed = line.trim();

    // Blank line
    if (trimmed === "") { i++; continue; }

    // Horizontal rule
    if (/^[-*_]{3,}$/.test(trimmed)) {
      elements.push(<hr key={key++} style={md.hr} />);
      i++; continue;
    }

    // Headings
    if (trimmed.startsWith("### ")) {
      elements.push(<h4 key={key++} style={md.h3}>{parseInline(trimmed.slice(4))}</h4>);
      i++; continue;
    }
    if (trimmed.startsWith("## ") && !trimmed.startsWith("### ")) {
      elements.push(<h3 key={key++} style={md.h2}>{parseInline(trimmed.slice(3))}</h3>);
      i++; continue;
    }
    if (trimmed.startsWith("# ") && !trimmed.startsWith("## ")) {
      elements.push(<h2 key={key++} style={md.h1}>{parseInline(trimmed.slice(2))}</h2>);
      i++; continue;
    }

    // Blockquote
    if (trimmed.startsWith("> ")) {
      const quoteLines = [];
      while (i < lines.length && lines[i].trim().startsWith("> ")) {
        quoteLines.push(lines[i].trim().slice(2));
        i++;
      }
      elements.push(
        <blockquote key={key++} style={md.blockquote}>
          {quoteLines.map((ql, qi) => <p key={qi} style={{ margin: 0 }}>{parseInline(ql)}</p>)}
        </blockquote>
      );
      continue;
    }

    // ── Markdown table ─────────────────────────────────────────
    // A table line starts with |
    if (trimmed.startsWith("|")) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        tableLines.push(lines[i].trim());
        i++;
      }
      const tableEl = parseTable(tableLines, key++);
      if (tableEl) elements.push(tableEl);
      continue;
    }

    // Bullet list
    if (/^\s*[-*•]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*•]\s+/.test(lines[i])) {
        const indent  = lines[i].match(/^(\s*)/)[1].length;
        const content = lines[i].replace(/^\s*[-*•]\s+/, "");
        items.push(
          <li key={i} style={{ ...md.li, paddingLeft: indent > 0 ? 8 : 0 }}>
            {parseInline(content)}
          </li>
        );
        i++;
      }
      elements.push(<ul key={key++} style={md.ul}>{items}</ul>);
      continue;
    }

    // Numbered list
    if (/^\d+\.\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(
          <li key={i} style={md.li}>{parseInline(lines[i].replace(/^\d+\.\s+/, ""))}</li>
        );
        i++;
      }
      elements.push(<ol key={key++} style={md.ol}>{items}</ol>);
      continue;
    }

    // Normal paragraph
    const paraLines = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !lines[i].trim().startsWith("|") &&
      !/^(#{1,3}\s|>\s|\s*[-*•]\s|\d+\.\s|[-*_]{3,})/.test(lines[i].trim())
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length > 0) {
      elements.push(
        <p key={key++} style={md.p}>{parseInline(paraLines.join(" "))}</p>
      );
    }
  }

  return <div style={md.root}>{elements}</div>;
}